import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PortalLayout from '../../components/PortalLayout';
import { adminAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface SettingsFormProps {
  title: string;
  description?: string;
  fields: {
    key: string;
    label: string;
    type?: 'text' | 'password' | 'number' | 'select' | 'email';
    options?: { value: string; label: string }[];
    helperText?: string;
    placeholder?: string;
  }[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onSave: () => void;
  isSaving: boolean;
}

const SettingsForm: React.FC<SettingsFormProps> = ({
  title,
  description,
  fields,
  values,
  onChange,
  onSave,
  isSaving,
}) => {
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  const toggleShow = (key: string) => {
    setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}
      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={2}>
        {fields.map((field) => (
          <Grid size={{ xs: 12, sm: field.type === 'select' ? 6 : 12, md: field.type === 'number' ? 4 : field.type === 'select' ? 6 : 12 }} key={field.key}>
            {field.type === 'select' ? (
              <FormControl fullWidth size="small">
                <InputLabel>{field.label}</InputLabel>
                <Select
                  value={values[field.key] || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  label={field.label}
                >
                  {(field.options || []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                size="small"
                label={field.label}
                type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                value={values[field.key] || ''}
                onChange={(e) => onChange(field.key, e.target.value)}
                helperText={field.helperText}
                placeholder={field.placeholder}
                InputProps={
                  field.type === 'password'
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => toggleShow(field.key)}>
                              {showPasswords[field.key] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            )}
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={isSaving}
          sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700 }}
        >
          {isSaving ? 'Wird gespeichert...' : 'Speichern'}
        </Button>
      </Box>
    </Paper>
  );
};

const AdminSettingsPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState<Record<string, boolean>>({});
  const [settings, setSettings] = useState<Record<string, string>>({});

  const loadSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await adminAPI.getSettings();
      setSettings(res.data.data || {});
    } catch {
      showError('Fehler beim Laden der Einstellungen');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (key: string, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (sectionKey: string, keys: string[]) => {
    setIsSaving((prev) => ({ ...prev, [sectionKey]: true }));
    try {
      const subset: Record<string, string> = {};
      keys.forEach((k) => { subset[k] = settings[k] || ''; });
      await adminAPI.setSettings(subset);
      showSuccess('Einstellungen gespeichert');
      await loadSettings();
    } catch {
      showError('Fehler beim Speichern');
    } finally {
      setIsSaving((prev) => ({ ...prev, [sectionKey]: false }));
    }
  };

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<{ data: string; mimeType: string } | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [faviconFile, setFaviconFile] = useState<{ data: string; mimeType: string } | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    setPreview: (v: string | null) => void,
    setFile: (v: { data: string; mimeType: string } | null) => void,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      setPreview(result);
      setFile({ data: base64, mimeType: file.type });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;
    setIsSaving((prev) => ({ ...prev, logo: true }));
    try {
      await adminAPI.uploadLogo(logoFile.data, logoFile.mimeType);
      showSuccess('Logo erfolgreich hochgeladen');
      setLogoFile(null);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Fehler beim Hochladen des Logos');
    } finally {
      setIsSaving((prev) => ({ ...prev, logo: false }));
    }
  };

  const handleUploadFavicon = async () => {
    if (!faviconFile) return;
    setIsSaving((prev) => ({ ...prev, favicon: true }));
    try {
      await adminAPI.uploadFavicon(faviconFile.data, faviconFile.mimeType);
      showSuccess('Favicon erfolgreich hochgeladen');
      setFaviconFile(null);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Fehler beim Hochladen des Favicons');
    } finally {
      setIsSaving((prev) => ({ ...prev, favicon: false }));
    }
  };

  const tabs = ['Allgemein', 'Erscheinungsbild', 'E-Mail / SMTP', 'Stripe', 'PayPal'];

  if (isLoading) {
    return (
      <PortalLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress />
        </Box>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <Box>
        <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
          Einstellungen
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Portal-Konfiguration, Zahlungsdienste und E-Mail-Einstellungen verwalten
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Änderungen werden sofort wirksam. Passwörter und geheime Schlüssel werden maskiert angezeigt – geben Sie einen neuen Wert ein, um ihn zu überschreiben.
        </Alert>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: '1px solid #e0e0e0' }}
          >
            {tabs.map((t) => (
              <Tab key={t} label={t} />
            ))}
          </Tabs>
        </Paper>

        {activeTab === 0 && (
          <>
            <SettingsForm
              title="Allgemeine Portal-Einstellungen"
              description="Grundlegende Konfiguration des Portals"
              fields={[
                {
                  key: 'frontend_url',
                  label: 'Frontend-URL',
                  type: 'text',
                  helperText: 'Öffentliche URL des Portals (z.B. https://callcenter-fuchs.com)',
                  placeholder: 'https://callcenter-fuchs.com',
                },
                {
                  key: 'callback_notification_email',
                  label: 'Rückruf-Benachrichtigungs-E-Mail',
                  type: 'email',
                  helperText: 'An diese Adresse werden neue Rückrufanfragen gesendet',
                  placeholder: 'admin@callcenter-fuchs.de',
                },
              ]}
              values={settings}
              onChange={handleChange}
              onSave={() => handleSave('general', ['frontend_url', 'callback_notification_email'])}
              isSaving={!!isSaving['general']}
            />

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Aktuelle Einstellungsübersicht
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(settings)
                  .filter(([k]) => !['form_config'].includes(k))
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${key}: ${value || '(leer)'}`}
                      variant="outlined"
                      size="small"
                      color={value ? 'default' : 'warning'}
                    />
                  ))}
              </Box>
            </Paper>
          </>
        )}

        {activeTab === 1 && (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Seitenlogo
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Das Logo erscheint in der Navigation und im Header. Empfohlen: PNG mit transparentem Hintergrund, max. 5 MB.
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Aktuelles Logo
                  </Typography>
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, backgroundColor: '#f8f9fa', minWidth: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                      src={logoPreview || `/callcenter-suche-logo.png?t=${Date.now()}`}
                      alt="Logo"
                      style={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e, setLogoPreview, setLogoFile)}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    Datei auswählen
                  </Button>
                  {logoFile && (
                    <Button
                      variant="contained"
                      startIcon={isSaving['logo'] ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleUploadLogo}
                      disabled={!!isSaving['logo']}
                      sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700 }}
                    >
                      {isSaving['logo'] ? 'Wird hochgeladen...' : 'Logo hochladen'}
                    </Button>
                  )}
                  {logoPreview && !logoFile && (
                    <Typography variant="caption" color="success.main">Logo hochgeladen</Typography>
                  )}
                </Box>
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Favicon
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Das Favicon erscheint im Browser-Tab. Empfohlen: ICO oder PNG, 32×32 px oder 64×64 px, max. 5 MB.
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Aktuelles Favicon
                  </Typography>
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, backgroundColor: '#f8f9fa', width: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                      src={faviconPreview || `/favicon.png?t=${Date.now()}`}
                      alt="Favicon"
                      style={{ width: 48, height: 48, objectFit: 'contain' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/gif,.ico"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e, setFaviconPreview, setFaviconFile)}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => faviconInputRef.current?.click()}
                  >
                    Datei auswählen
                  </Button>
                  {faviconFile && (
                    <Button
                      variant="contained"
                      startIcon={isSaving['favicon'] ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleUploadFavicon}
                      disabled={!!isSaving['favicon']}
                      sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700 }}
                    >
                      {isSaving['favicon'] ? 'Wird hochgeladen...' : 'Favicon hochladen'}
                    </Button>
                  )}
                  {faviconPreview && !faviconFile && (
                    <Typography variant="caption" color="success.main">Favicon hochgeladen</Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          </>
        )}

        {activeTab === 2 && (
          <SettingsForm
            title="E-Mail / SMTP Konfiguration"
            description="Einstellungen für den ausgehenden E-Mail-Versand. Änderungen werden beim nächsten E-Mail-Versand wirksam."
            fields={[
              {
                key: 'smtp_host',
                label: 'SMTP-Host',
                type: 'text',
                helperText: 'Hostname des Mail-Servers',
                placeholder: 'smtp.example.com',
              },
              {
                key: 'smtp_port',
                label: 'SMTP-Port',
                type: 'number',
                helperText: 'Port des Mail-Servers (z.B. 25, 465, 587)',
                placeholder: '587',
              },
              {
                key: 'smtp_secure',
                label: 'TLS/SSL',
                type: 'select',
                options: [
                  { value: 'false', label: 'Nein (STARTTLS oder kein TLS)' },
                  { value: 'true', label: 'Ja (direkt SSL/TLS, Port 465)' },
                ],
                helperText: 'Direkte SSL-Verbindung (Port 465)',
              },
              {
                key: 'smtp_user',
                label: 'SMTP-Benutzername',
                type: 'text',
                helperText: 'E-Mail-Adresse oder Benutzername für die Authentifizierung',
                placeholder: 'user@example.com',
              },
              {
                key: 'smtp_password',
                label: 'SMTP-Passwort',
                type: 'password',
                helperText: 'Passwort für die SMTP-Authentifizierung',
              },
              {
                key: 'smtp_from',
                label: 'Absender-Adresse (From)',
                type: 'email',
                helperText: 'E-Mail-Adresse, die als Absender erscheint',
                placeholder: 'noreply@callcenter-fuchs.de',
              },
            ]}
            values={settings}
            onChange={handleChange}
            onSave={() => handleSave('smtp', ['smtp_host', 'smtp_port', 'smtp_secure', 'smtp_user', 'smtp_password', 'smtp_from'])}
            isSaving={!!isSaving['smtp']}
          />
        )}

        {activeTab === 3 && (
          <>
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, backgroundColor: '#fffbf0', borderColor: '#f0c060' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#b45309' }}>
              Wo finde ich meine Stripe-Zugangsdaten?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon fontSize="small" />}
                href="https://dashboard.stripe.com/apikeys"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ alignSelf: 'flex-start', borderColor: '#b45309', color: '#b45309', textTransform: 'none', '&:hover': { borderColor: '#92400e', backgroundColor: '#fef3c7' } }}
              >
                Stripe Dashboard → API-Schlüssel (Publishable Key &amp; Secret Key)
              </Button>
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon fontSize="small" />}
                href="https://dashboard.stripe.com/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ alignSelf: 'flex-start', borderColor: '#b45309', color: '#b45309', textTransform: 'none', '&:hover': { borderColor: '#92400e', backgroundColor: '#fef3c7' } }}
              >
                Stripe Dashboard → Webhooks (Webhook Secret)
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: '#78350f', mt: 1.5, display: 'block' }}>
              Für Tests verwenden Sie Schlüssel die mit <strong>pk_test_</strong> / <strong>sk_test_</strong> beginnen. Für den Produktivbetrieb benötigen Sie <strong>pk_live_</strong> / <strong>sk_live_</strong>.
            </Typography>
          </Paper>
          <SettingsForm
            title="Stripe Zahlungseinstellungen"
            description="API-Schlüssel für die Stripe-Zahlungsabwicklung. Verwenden Sie Test-Schlüssel (sk_test_...) für die Entwicklung und Live-Schlüssel (sk_live_...) für die Produktion."
            fields={[
              {
                key: 'stripe_publishable_key',
                label: 'Publishable Key (öffentlich)',
                type: 'text',
                helperText: 'Beginnt mit pk_test_ (Test) oder pk_live_ (Live) – wird im Frontend verwendet',
                placeholder: 'pk_test_...',
              },
              {
                key: 'stripe_secret_key',
                label: 'Secret Key (geheim)',
                type: 'password',
                helperText: 'Beginnt mit sk_test_ (Test) oder sk_live_ (Live) – nur serverseitig',
              },
              {
                key: 'stripe_webhook_secret',
                label: 'Webhook Secret',
                type: 'password',
                helperText: 'Webhook-Signaturschlüssel aus dem Stripe Dashboard (beginnt mit whsec_)',
              },
            ]}
            values={settings}
            onChange={handleChange}
            onSave={() => handleSave('stripe', ['stripe_publishable_key', 'stripe_secret_key', 'stripe_webhook_secret'])}
            isSaving={!!isSaving['stripe']}
          />
          </>
        )}

        {activeTab === 4 && (
          <>
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, backgroundColor: '#f0f6ff', borderColor: '#7ab3e8' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#003087' }}>
              Wo finde ich meine PayPal-Zugangsdaten?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon fontSize="small" />}
                href="https://developer.paypal.com/dashboard/applications/sandbox"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ alignSelf: 'flex-start', borderColor: '#003087', color: '#003087', textTransform: 'none', '&:hover': { borderColor: '#001f5b', backgroundColor: '#e8f0fe' } }}
              >
                PayPal Developer Dashboard → Apps &amp; Credentials (Client ID &amp; Secret)
              </Button>
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon fontSize="small" />}
                href="https://developer.paypal.com/dashboard/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ alignSelf: 'flex-start', borderColor: '#003087', color: '#003087', textTransform: 'none', '&:hover': { borderColor: '#001f5b', backgroundColor: '#e8f0fe' } }}
              >
                PayPal Developer Dashboard → Webhooks (Webhook ID)
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: '#003087', mt: 1.5, display: 'block' }}>
              Wählen Sie unter "Apps &amp; Credentials" Ihre App aus (oder erstellen Sie eine neue) und kopieren Sie <strong>Client ID</strong> und <strong>Secret</strong>. Für Tests nutzen Sie den <strong>Sandbox</strong>-Modus.
            </Typography>
          </Paper>
          <SettingsForm
            title="PayPal Zahlungseinstellungen"
            description="API-Zugangsdaten für die PayPal-Zahlungsabwicklung. Zugangsdaten finden Sie im PayPal Developer Dashboard."
            fields={[
              {
                key: 'paypal_client_id',
                label: 'Client ID',
                type: 'text',
                helperText: 'PayPal App Client ID aus dem Developer Dashboard',
                placeholder: 'AaBbCcDd...',
              },
              {
                key: 'paypal_client_secret',
                label: 'Client Secret',
                type: 'password',
                helperText: 'PayPal App Client Secret – nur serverseitig',
              },
              {
                key: 'paypal_webhook_id',
                label: 'Webhook ID',
                type: 'text',
                helperText: 'Webhook-ID aus dem PayPal Developer Dashboard',
                placeholder: 'XXXXXXXXXXXXXXXX',
              },
              {
                key: 'paypal_mode',
                label: 'Modus',
                type: 'select',
                options: [
                  { value: 'sandbox', label: 'Sandbox (Test)' },
                  { value: 'live', label: 'Live (Produktion)' },
                ],
                helperText: 'Sandbox für Tests, Live für echte Zahlungen',
              },
            ]}
            values={settings}
            onChange={handleChange}
            onSave={() => handleSave('paypal', ['paypal_client_id', 'paypal_client_secret', 'paypal_webhook_id', 'paypal_mode'])}
            isSaving={!!isSaving['paypal']}
          />
          </>
        )}
      </Box>
    </PortalLayout>
  );
};

export default AdminSettingsPage;
